import * as XLSX from 'xlsx';
import { Movement } from '../../types/movement';
import { Pilgrim } from '../../types/pilgrim';

type ExportRow = {
  pilgrim_name: string;
  pilgrim_passport: string;
  pilgrim_gender: string;
  pilgrim_country: string;
  pilgrim_package: string;
  pilgrim_mobile: string;
  movement_date: string;
  movement_time: string;
  from_location: string;
  to_location: string;
  estimated_arrival: string;
};

export const exportMovementDetails = (movement: Movement, pilgrims: Pilgrim[], translations: any) => {
  try {
    console.log('ExportService - Starting export:', {
      movementType: movement.type,
      pilgrimsCount: pilgrims.length
    });

    const rows = createExportRows(movement, pilgrims, translations);
    console.log('Created export rows:', {
      rowCount: rows.length,
      sampleRow: rows[0]
    });

    const ws = createWorksheet(rows, translations);
    const wb = createWorkbook(ws, movement.type, translations);
    
    console.log('Created workbook, saving...');
    saveWorkbook(wb, movement.type);
    console.log('Export completed successfully');
  } catch (error) {
    console.error('Error in exportMovementDetails:', error);
    throw new Error(translations?.movementExport?.exportError || 'Failed to export details');
  }
};

const createExportRows = (movement: Movement, pilgrims: Pilgrim[], translations: any): ExportRow[] => {
  const getGenderLabel = (gender: string): string => {
    return gender === 'male' 
      ? translations?.common?.male || 'Male'
      : translations?.common?.female || 'Female';
  };

  return pilgrims.map(pilgrim => ({
    pilgrim_name: pilgrim.Name,
    pilgrim_passport: pilgrim.Passport_Number,
    pilgrim_gender: getGenderLabel(pilgrim.Gender),
    pilgrim_country: pilgrim.Residence_Country,
    pilgrim_package: pilgrim.package_nickName,
    pilgrim_mobile: pilgrim.Mobile_Number,
    movement_date: movement.departureDate,
    movement_time: movement.departureTime,
    from_location: `${movement.from.name} (${getLocationLabel(movement.from.city, translations)})`,
    to_location: `${movement.to.name} (${getLocationLabel(movement.to.city, translations)})`,
    estimated_arrival: movement.arrivalTime
  }));
};

const createWorksheet = (rows: ExportRow[], translations: any): XLSX.WorkSheet => {
  const ws = XLSX.utils.json_to_sheet(rows, {
    header: [
      'pilgrim_name',
      'pilgrim_passport',
      'pilgrim_gender',
      'pilgrim_country',
      'pilgrim_package',
      'pilgrim_mobile',
      'movement_date',
      'movement_time',
      'from_location',
      'to_location',
      'estimated_arrival'
    ]
  });

  // Add localized headers
  XLSX.utils.sheet_add_aoa(ws, [[
    translations?.movementExport?.headers?.name || 'Pilgrim Name',
    translations?.movementExport?.headers?.passport || 'Passport Number',
    translations?.movementExport?.headers?.gender || 'Gender',
    translations?.movementExport?.headers?.country || 'Country',
    translations?.movementExport?.headers?.package || 'Package',
    translations?.movementExport?.headers?.mobile || 'Mobile Number',
    translations?.movementExport?.headers?.date || 'Movement Date',
    translations?.movementExport?.headers?.time || 'Movement Time',
    translations?.movementExport?.headers?.from || 'From',
    translations?.movementExport?.headers?.to || 'To',
    translations?.movementExport?.headers?.arrival || 'Estimated Arrival'
  ]], { origin: 'A1' });

  // Set column widths
  ws['!cols'] = [
    { wch: 30 }, // name
    { wch: 15 }, // passport
    { wch: 10 }, // gender
    { wch: 20 }, // country
    { wch: 25 }, // package
    { wch: 15 }, // mobile
    { wch: 12 }, // date
    { wch: 12 }, // time
    { wch: 30 }, // from
    { wch: 30 }, // to
    { wch: 12 }, // arrival
  ];

  return ws;
};

const createWorkbook = (ws: XLSX.WorkSheet, movementType: string, translations: any): XLSX.WorkBook => {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, getSheetName(movementType, translations));
  return wb;
};

const saveWorkbook = (wb: XLSX.WorkBook, movementType: string) => {
  const date = new Date().toISOString().split('T')[0];
  const filename = `movement-details-${movementType}-${date}.xlsx`;
  XLSX.writeFile(wb, filename);
};

const getLocationLabel = (city: string, translations: any): string => {
  switch (city) {
    case 'makkah': return translations?.cities?.makkah || 'Makkah';
    case 'madinah': return translations?.cities?.madinah || 'Madinah';
    case 'jeddah': return translations?.cities?.jeddah || 'Jeddah';
    default: return city;
  }
};

const getSheetName = (type: string, translations: any): string => {
  switch (type) {
    case 'arrival': 
      return translations?.movementTypes?.arrival || 'Arrival Details';
    case 'departure': 
      return translations?.movementTypes?.departure || 'Departure Details';
    case 'hotel_transfer': 
      return translations?.movementTypes?.hotelTransfer || 'Hotel Transfer Details';
    case 'camp_departure': 
      return translations?.movementTypes?.campDeparture || 'Camp Departure Details';
    case 'camp_return': 
      return translations?.movementTypes?.campReturn || 'Camp Return Details';
    default: 
      return translations?.movementTypes?.default || 'Movement Details';
  }
};